import { useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { SearchLogisticsObjectsRequest } from '@/api/bol';
import {
  decodeSearchQueryFromUrl,
  encodeSearchQueryForUrl,
} from '@/utils/logisticsObjectSearchUrl';

const DEFAULT_SEARCH_QUERY_PARAM = 'search';

interface UseLogisticsObjectSearchUrlOptions {
  queryParamName?: string;
}

interface CopySearchLinkHandlers {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export const useLogisticsObjectSearchUrl = (
  options: UseLogisticsObjectSearchUrlOptions = {},
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const queryParamName = options.queryParamName ?? DEFAULT_SEARCH_QUERY_PARAM;

  const getSearchQueryFromUrl = useCallback((): SearchLogisticsObjectsRequest | undefined => {
    return decodeSearchQueryFromUrl(searchParams.get(queryParamName));
  }, [queryParamName, searchParams]);

  const updateSearchQueryInUrl = useCallback((searchQuery: SearchLogisticsObjectsRequest) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    const encodedSearchQuery = encodeSearchQueryForUrl(searchQuery);

    if (encodedSearchQuery) {
      nextSearchParams.set(queryParamName, encodedSearchQuery);
    } else {
      nextSearchParams.delete(queryParamName);
    }

    setSearchParams(nextSearchParams);
  }, [queryParamName, searchParams, setSearchParams]);

  const buildShareableSearchLink = useCallback((searchQuery: SearchLogisticsObjectsRequest): string => {
    const nextSearchParams = new URLSearchParams(searchParams);
    const encodedSearchQuery = encodeSearchQueryForUrl(searchQuery);

    if (encodedSearchQuery) {
      nextSearchParams.set(queryParamName, encodedSearchQuery);
    } else {
      nextSearchParams.delete(queryParamName);
    }

    const queryString = nextSearchParams.toString();
    const relativeUrl = `${location.pathname}${queryString ? `?${queryString}` : ''}`;

    if (typeof window === 'undefined') {
      return relativeUrl;
    }

    return `${window.location.origin}${relativeUrl}`;
  }, [location.pathname, queryParamName, searchParams]);

  const copyShareableSearchLink = useCallback(async (
    searchQuery: SearchLogisticsObjectsRequest,
    handlers: CopySearchLinkHandlers = {},
  ) => {
    try {
      const shareableLink = buildShareableSearchLink(searchQuery);
      await navigator.clipboard.writeText(shareableLink);
      handlers.onSuccess?.('Search link copied to clipboard');
    } catch (error) {
      console.error('Failed to copy search link:', error);
      handlers.onError?.('Failed to copy search link to clipboard');
    }
  }, [buildShareableSearchLink]);

  return {
    getSearchQueryFromUrl,
    updateSearchQueryInUrl,
    buildShareableSearchLink,
    copyShareableSearchLink,
  };
};
